import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useSeo } from '../hooks/useSeo';
import {
  Dog,
  CalendarHeart,
  CalendarClock,
  ClipboardList,
  GitBranch,
  Users,
  Building2,
  CalendarDays,
  ShieldCheck,
  ArrowRight,
  Check,
  Sparkles,
} from 'lucide-react';
import { PublicLayout } from '../components/public/PublicLayout';
import { SectionHeader } from '../components/public/SectionHeader';

const FEATURE_ICONS = [Dog, CalendarHeart, CalendarClock, ClipboardList, GitBranch, Users, Building2, CalendarDays, ShieldCheck];
const FEATURE_KEYS = [
  'dogManagement',
  'breedingPlanning',
  'pregnancy',
  'litterDocs',
  'pedigree',
  'prospects',
  'breedingStation',
  'planner',
  'secure',
] as const;

const PACKAGE_KEYS = ['free', 'pro', 'station'] as const;

const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSeo({ title: t('pricing:meta.title'), description: t('pricing:meta.description'), path: '/pakete', lang: i18n.resolvedLanguage });

  return (
    <PublicLayout>
      {({ openRegister }) => (
        <>
          {/* Hero mit Beta-Hinweis */}
          <section className="relative -mt-16 overflow-hidden pt-16 lg:-mt-[4.5rem] lg:pt-[4.5rem]">
            <div aria-hidden="true" className="landing-grid pointer-events-none absolute inset-0" />
            <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-6 sm:pt-16 lg:px-8 lg:pb-20">
              <div className="mx-auto max-w-3xl text-center">
                <h1 className="text-balance text-4xl font-extrabold leading-[1.08] tracking-tight text-dark-50 sm:text-5xl">
                  {t('pricing:hero.title')}
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-dark-300">{t('pricing:hero.subtitle')}</p>
              </div>

              <div className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-accent-500/30 bg-accent-500/10 px-5 py-4 text-left">
                <span className="badge badge-warning mt-0.5 shrink-0">{t('pricing:beta.badge')}</span>
                <div>
                  <p className="font-semibold text-dark-50">{t('pricing:beta.title')}</p>
                  <p className="mt-1 text-sm text-dark-300">{t('pricing:beta.description')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Pakete */}
          <section className="border-t border-dark-700/50 py-20 sm:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <SectionHeader title={t('pricing:packages.sectionTitle')} subtitle={t('pricing:packages.sectionSubtitle')} />

              <div className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-3">
                {PACKAGE_KEYS.map((key) => {
                  const isHighlighted = key === 'pro';
                  const badge = t(`pricing:packages.${key}.badge`, { defaultValue: '' });
                  const features = t(`pricing:packages.${key}.features`, { returnObjects: true }) as string[];

                  return (
                    <div
                      key={key}
                      className={clsx(
                        'card relative flex flex-col gap-6',
                        isHighlighted && 'border-primary-500/60 ring-2 ring-primary-500/40'
                      )}
                    >
                      {badge && (
                        <span className="badge badge-primary absolute -top-3 right-6 inline-flex items-center gap-1">
                          <Sparkles size={12} />
                          {badge}
                        </span>
                      )}

                      <div>
                        <h3 className="text-xl font-bold text-dark-50">{t(`pricing:packages.${key}.name`)}</h3>
                        <p className="mt-1 text-sm text-dark-400">{t(`pricing:packages.${key}.tagline`)}</p>
                      </div>

                      <div className="border-y border-dark-700/60 py-4">
                        <div className="text-lg font-semibold text-primary-300">{t('pricing:packages.priceLabel')}</div>
                        <div className="mt-1 text-xs text-dark-400">{t('pricing:packages.priceNote')}</div>
                      </div>

                      <ul className="flex flex-1 flex-col gap-3">
                        {features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2.5 text-sm text-dark-200">
                            <Check size={16} className="mt-0.5 shrink-0 text-primary-400" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={openRegister}
                        className={clsx('btn w-full', isHighlighted ? 'btn-primary' : 'btn-outline')}
                      >
                        {t(`pricing:packages.${key}.cta`)}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Funktionen */}
          <section className="border-t border-dark-700/50 bg-dark-950/25 py-20 sm:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <SectionHeader title={t('pricing:features.sectionTitle')} subtitle={t('pricing:features.sectionSubtitle')} />

              <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {FEATURE_KEYS.map((key, index) => {
                  const Icon = FEATURE_ICONS[index];
                  return (
                    <div key={key} className="card flex items-start gap-4">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary-400/20 bg-primary-500/10 text-primary-300">
                        <Icon size={22} />
                      </span>
                      <div>
                        <h3 className="font-semibold text-dark-50">{t(`pricing:features.items.${key}.title`)}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-dark-400">
                          {t(`pricing:features.items.${key}.description`)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Abschluss */}
          <section className="px-4 py-24 sm:px-6 lg:px-8">
            <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl border border-primary-400/20 bg-gradient-to-br from-primary-600/25 via-dark-800/60 to-secondary-600/20 px-6 py-16 text-center sm:px-12 sm:py-20">
              <div aria-hidden="true" className="landing-grid pointer-events-none absolute inset-0" />
              <div className="relative mx-auto max-w-3xl">
                <h2 className="text-balance text-3xl font-bold tracking-tight text-dark-50 sm:text-4xl">
                  {t('pricing:waitlist.title')}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-dark-300 sm:text-lg">{t('pricing:waitlist.subtitle')}</p>
                <button onClick={openRegister} className="btn btn-primary btn-lg mt-9 w-full sm:w-auto">
                  {t('pricing:waitlist.button')}
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </PublicLayout>
  );
};

export default PricingPage;
